import { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDialog } from '@/common/context/DialogContext';
import { useTranslation } from 'react-i18next';
import { getOralExamApiErrorCode, getOralExamApiErrorMessage } from './oralExamApiError';
import { unwrapOralExamCohortState } from './unwrapOralExamCohortState';
import { unwrapOralExamRunState } from './useOralExamRunStatePoll';

const SCREEN_DEDICATED_ERROR_CODES = new Set([
  'session_already_ended',
  'session_cancelled_supervisor_absent',
  'too_early',
  'late_join_cutoff',
  'session_started_without_you',
  'expired',
]);

export function isIllegalStateError(error: unknown): boolean {
  const code = getOralExamApiErrorCode(error);
  return code === 'invalid_state_transition';
}

/** True when the server rejected a stale booking If-Match (retryable once). */
export function isStaleEtagError(error: unknown): boolean {
  if (isIllegalStateError(error)) return false;
  const status = error instanceof AxiosError ? error.response?.status : undefined;
  if (status !== 412) return false;
  const code = getOralExamApiErrorCode(error);
  return code === 'etag_mismatch' || code === undefined;
}

export async function fetchFreshBookingEtag(
  queryClient: QueryClient,
  stateQueryKey: readonly unknown[],
  /** When set, read etag from a cohort booking row instead of a booking run-state poll. */
  cohortBookingId?: string,
): Promise<string | null> {
  await queryClient.refetchQueries({ queryKey: stateQueryKey });
  const data = queryClient.getQueryData(stateQueryKey);
  if (cohortBookingId) {
    const cohort = unwrapOralExamCohortState(data);
    const row = cohort?.bookingRuns.find((run) => run.bookingId === cohortBookingId);
    return row?.runState?.etag ?? null;
  }
  const runState = unwrapOralExamRunState(data);
  return runState?.etag ?? null;
}

export function useOralExamMutationErrorHandler(options: {
  stateQueryKey: readonly unknown[];
}) {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const queryClient = useQueryClient();

  const refreshRunState = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: options.stateQueryKey });
  }, [options.stateQueryKey, queryClient]);

  const handleStale = useCallback(() => {
    showToastMessage('warning', t('oralExam.staleStateToast'));
    refreshRunState();
  }, [refreshRunState, showToastMessage, t]);

  const onMutationError = useCallback((error: unknown) => {
    const status = error instanceof AxiosError ? error.response?.status : undefined;
    if (status === 412) {
      handleStale();
      return;
    }
    const code = getOralExamApiErrorCode(error);
    if (code && SCREEN_DEDICATED_ERROR_CODES.has(code)) {
      refreshRunState();
      return;
    }
    if (code === 'question_pool_empty') {
      showToastMessage('error', t('oralExam.questionPoolEmpty'));
      return;
    }
    const message = getOralExamApiErrorMessage(
      error,
      t('oralExam.actionFailed', { defaultValue: 'Action failed' }),
    );
    // Drop-in / integrity pauses can reject Next with a raw state-machine string —
    // surface a clearer toast (MeetingConfirm stays for destructive confirms).
    if (/paused/i.test(message)) {
      showToastMessage('warning', t('oralExam.examStillPaused'));
      return;
    }
    showToastMessage('error', message);
  }, [handleStale, refreshRunState, showToastMessage, t]);

  const withStaleRetry = useCallback(async <T>(
    fn: (etag: string | null | undefined) => Promise<T>,
    initialEtag: string | null | undefined,
    /** Booking run-state key for booking writes; defaults to hook `stateQueryKey`. */
    retryQueryKey?: readonly unknown[],
    /** Cohort row booking id — retry If-Match from bookingRuns[i].runState.etag. */
    cohortBookingId?: string,
  ): Promise<T> => {
    const etagQueryKey = retryQueryKey ?? options.stateQueryKey;
    try {
      return await fn(initialEtag);
    } catch (error) {
      if (!isStaleEtagError(error)) {
        onMutationError(error);
        throw error;
      }
      const freshEtag = await fetchFreshBookingEtag(queryClient, etagQueryKey, cohortBookingId);
      try {
        return await fn(freshEtag);
      } catch (retryError) {
        onMutationError(retryError);
        throw retryError;
      }
    }
  }, [onMutationError, options.stateQueryKey, queryClient]);

  return {
    onMutationError,
    handleStale,
    withStaleRetry,
  };
}
